import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchUserTopSearches } from '../api/analytics';
import { fetchUserProfile, updateUserProfile, clearSearchHistory, exportUserData, deleteAccount } from '../api/user';
import { getConnectionStatus } from '../api/oauth';
import { supabase } from '../api/supabase';
import PageLoader from './PageLoader.jsx';
import { useAuth } from './AuthContext.jsx';
import NotificationPreferences from './NotificationPreferences.jsx';
import { useNotifications } from './NotificationContext.jsx';
import AccountConnections from './AccountConnections.jsx';
import UserMenu from './UserMenu.jsx';
import LegalFooter from './LegalFooter.jsx';
import GlanceStats from './profile/GlanceStats.jsx';
import ComingUpList from './profile/ComingUpList.jsx';
import IdentityEditor from './profile/IdentityEditor.jsx';
import {
    displayNameFor,
    formatMemberSince,
    identityFromAuthUser,
    identityFromMe,
    initialsFromName,
    isUpcomingEventSubscription,
} from './profile/profileUtils.js';
import '../styles/appHeader.css';
import '../styles/resultsPage.css';
import '../styles/profilePage.css';

const TABS = [
    { key: 'you', label: 'You' },
    { key: 'alerts', label: 'Alerts' },
    { key: 'account', label: 'Account' },
];

function tabFromParam(value) {
    if (value === 'notifications') return 'alerts';
    if (value === 'profile') return 'you';
    if (TABS.some((tab) => tab.key === value)) return value;
    return 'you';
}

const Profile = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { logout, user: authUser, loading: authLoading } = useAuth();
    const { subscriptions, platformChannels } = useNotifications();

    const [profile, setProfile] = useState(null);
    const [email, setEmail] = useState('');
    const [createdAt, setCreatedAt] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(() => tabFromParam(searchParams.get('tab')));
    const [highlightChannels, setHighlightChannels] = useState(searchParams.get('focus') === 'channels');
    const [sheet, setSheet] = useState(null);
    const [taste, setTaste] = useState([]);
    const [connections, setConnections] = useState({ youtube: false, twitch: false, kick: false });
    const [pwMessage, setPwMessage] = useState(null);
    const [pwSending, setPwSending] = useState(false);
    const [dangerBusy, setDangerBusy] = useState(false);
    const [dangerError, setDangerError] = useState(null);

    useEffect(() => {
        const next = tabFromParam(searchParams.get('tab'));
        setActiveTab(next);
        setHighlightChannels(searchParams.get('focus') === 'channels');
    }, [searchParams]);

    const goTab = useCallback((key, extras = {}) => {
        const params = { tab: key, ...extras };
        setActiveTab(key);
        setHighlightChannels(extras.focus === 'channels');
        setSearchParams(params, { replace: true });
        if (key !== 'alerts') setHighlightChannels(false);
        setSheet(null);
    }, [setSearchParams]);

    useEffect(() => {
        let mounted = true;

        if (authUser?.email) {
            const fromAuth = identityFromAuthUser(authUser);
            setEmail(fromAuth.email);
            setCreatedAt(fromAuth.createdAt);
            setProfile((prev) => ({
                ...(prev || {}),
                displayName: prev?.displayName || fromAuth.displayName,
                avatarUrl: prev?.avatarUrl || fromAuth.avatarUrl,
                email: fromAuth.email,
                createdAt: fromAuth.createdAt,
            }));
            setLoading(false);
        } else if (!authLoading) {
            setError('Please log in again');
            setLoading(false);
        }

        const load = async () => {
            try {
                const me = await fetchUserProfile();
                if (!mounted) return;
                const nextProfile = identityFromMe(me, identityFromAuthUser(authUser));
                setProfile(nextProfile);
                if (nextProfile.email) setEmail(nextProfile.email);
                if (nextProfile.createdAt) setCreatedAt(nextProfile.createdAt);
                setError(null);
            } catch (err) {
                if (!mounted) return;
                if (!authUser?.email) {
                    setError(err.message || 'Failed to load profile');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        fetchUserTopSearches('30d', 8)
            .then((data) => {
                if (!mounted) return;
                const rows = data?.top_searches || [];
                setTaste(rows.map((item) => ({ name: item.search_query, count: item.count })));
            })
            .catch(() => {
                if (mounted) setTaste([]);
            });
        getConnectionStatus()
            .then((status) => {
                if (mounted) setConnections(status);
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [authLoading, authUser?.id, authUser?.email, authUser?.created_at]);

    const displayName = displayNameFor(profile, email);
    const upcoming = useMemo(
        () => (subscriptions || []).filter(isUpcomingEventSubscription),
        [subscriptions]
    );
    const connectedCount = [connections.youtube, connections.twitch, connections.kick].filter(Boolean).length;
    const channelCounts = useMemo(() => ({
        youtube: (platformChannels || []).filter((ch) => ch.platform === 'youtube').length,
        twitch: (platformChannels || []).filter((ch) => ch.platform === 'twitch').length,
        kick: (platformChannels || []).filter((ch) => ch.platform === 'kick').length,
    }), [platformChannels]);

    const handleSaveIdentity = async ({ displayName: nextName, avatarUrl }) => {
        const result = await updateUserProfile({ displayName: nextName, avatarUrl });
        setProfile((prev) => ({ ...(prev || {}), ...(result.userProfile || { displayName: nextName, avatarUrl }) }));
        setSheet(null);
    };

    const handlePasswordReset = async () => {
        if (!email) return;
        setPwSending(true);
        setPwMessage(null);
        try {
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/#/`,
            });
            if (resetError) throw resetError;
            setPwMessage('Reset link sent. Check your email.');
        } catch (err) {
            setPwMessage(err.message || 'Could not send reset email');
        } finally {
            setPwSending(false);
        }
    };

    const handleClearHistory = async () => {
        setDangerBusy(true);
        setDangerError(null);
        try {
            await clearSearchHistory();
            setTaste([]);
            setSheet(null);
        } catch (err) {
            setDangerError(err.message || 'Could not clear search history');
        } finally {
            setDangerBusy(false);
        }
    };

    const handleExportData = async () => {
        setDangerBusy(true);
        setDangerError(null);
        try {
            const payload = await exportUserData();
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'livenow-data-export.json';
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setDangerError(err.message || 'Could not export data');
        } finally {
            setDangerBusy(false);
        }
    };

    const handleDeleteData = async () => {
        setDangerBusy(true);
        setDangerError(null);
        try {
            await deleteAccount();
            setSheet(null);
            logout();
            navigate('/', { replace: true });
        } catch (err) {
            setDangerError(err.message || 'Could not delete account');
        } finally {
            setDangerBusy(false);
        }
    };

    if (loading) return <PageLoader message="Loading Profile" />;

    if (error && !email) {
        return (
            <div className="profile-page-status">
                <div className="profile-error">Could not load profile</div>
                <p>{error}</p>
                <button type="button" className="profile-btn profile-btn--primary" onClick={() => navigate('/')}>
                    Go Home
                </button>
            </div>
        );
    }

    const identity = (
        <button type="button" className="profile-identity" onClick={() => setSheet('identity')}>
            <div
                className={`profile-avatar${activeTab === 'you' ? '' : ' profile-avatar--sm'}${profile?.avatarUrl ? ' profile-avatar--photo' : ''}`}
                style={profile?.avatarUrl ? { backgroundImage: `url("${profile.avatarUrl}")` } : undefined}
            >
                {profile?.avatarUrl ? null : <span>{initialsFromName(displayName)}</span>}
            </div>
            <div className="profile-identity-copy">
                <div className="profile-identity-name">{displayName}</div>
                <div className="profile-identity-email">{email}</div>
                {createdAt ? (
                    <div className="profile-identity-meta">
                        Member since {formatMemberSince(createdAt)} · tap to edit
                    </div>
                ) : (
                    <div className="profile-identity-meta">Tap to edit</div>
                )}
            </div>
        </button>
    );

    return (
        <div className="profile-page">
            <header className="app-header glass">
                <span className="logo-text" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>LiveNow</span>
                <div className="header-right">
                    <nav className="header-nav">
                        <UserMenu />
                    </nav>
                </div>
            </header>

            <div className="profile-layout">
                <aside className="profile-rail">
                    {identity}
                    <nav className="profile-rail-nav" aria-label="Profile">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`profile-rail-btn${tab.key === activeTab ? ' is-active' : ''}`}
                                onClick={() => goTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <div className="profile-main">
                    <div className="profile-mobile-tabs" data-tab={activeTab} role="tablist">
                        <div className="profile-mobile-pill" aria-hidden="true" />
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={tab.key === activeTab}
                                className={`profile-mobile-tab${tab.key === activeTab ? ' is-active' : ''}`}
                                onClick={() => {
                                    if (tab.key === activeTab) return;
                                    goTab(tab.key);
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'you' && (
                        <div className="profile-pane" key="you">
                            <div className="profile-mobile-identity">{identity}</div>
                            <GlanceStats
                                watching={upcoming.length}
                                channels={platformChannels?.length || 0}
                                platforms={connectedCount}
                            />
                            <div className="profile-section-head">
                                <h3>Coming up</h3>
                                <span>Upcoming only</span>
                            </div>
                            <ComingUpList
                                items={upcoming}
                                emptyHint="No upcoming reminders yet. Notify an event from search results to see it here."
                            />
                            <div className="profile-section-head">
                                <h3>Taste</h3>
                                <span>From your searches</span>
                            </div>
                            {taste.length === 0 ? (
                                <p className="profile-empty">No search data yet. Search something live to build taste chips.</p>
                            ) : (
                                <div className="profile-taste">
                                    {taste.map((item) => (
                                        <button
                                            key={item.name}
                                            type="button"
                                            className="profile-taste-chip"
                                            onClick={() => navigate('/', { state: { searchQuery: item.name } })}
                                        >
                                            {item.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'alerts' && (
                        <div className="profile-pane" key="alerts">
                            <NotificationPreferences highlightChannels={highlightChannels} />
                        </div>
                    )}

                    {activeTab === 'account' && (
                        <div className="profile-pane" key="account">
                            <div className="profile-section-head">
                                <h3>Connections</h3>
                            </div>
                            <AccountConnections
                                channelCounts={channelCounts}
                                onOpenChannels={() => goTab('alerts', { focus: 'channels' })}
                            />
                            <div className="profile-section-head">
                                <h3>Security</h3>
                            </div>
                            <div className="profile-settings-row">
                                <div className="profile-settings-copy">
                                    <div className="profile-settings-title">Password</div>
                                    <div className="profile-settings-hint">Send a reset link to {email || 'your email'}</div>
                                    {pwMessage ? <div className="profile-hint">{pwMessage}</div> : null}
                                </div>
                                <button
                                    type="button"
                                    className="profile-btn"
                                    onClick={handlePasswordReset}
                                    disabled={pwSending || !email}
                                >
                                    {pwSending ? 'Sending...' : 'Reset'}
                                </button>
                            </div>
                            <div className="profile-section-head">
                                <h3>Data</h3>
                            </div>
                            <button type="button" className="profile-settings-row" onClick={() => setSheet('clear')}>
                                <div className="profile-settings-copy">
                                    <div className="profile-settings-title">Clear search history</div>
                                    <div className="profile-settings-hint">Removes taste chips. Keeps alerts and connections.</div>
                                </div>
                            </button>
                            <button type="button" className="profile-settings-row" onClick={handleExportData} disabled={dangerBusy}>
                                <div className="profile-settings-copy">
                                    <div className="profile-settings-title">Download your data</div>
                                    <div className="profile-settings-hint">JSON export of your LiveNow account (no OAuth or push secrets).</div>
                                </div>
                            </button>
                            <button type="button" className="profile-settings-row" onClick={() => setSheet('delete')}>
                                <div className="profile-settings-copy">
                                    <div className="profile-settings-title" style={{ color: '#f87171' }}>Delete account</div>
                                    <div className="profile-settings-hint">Removes your profile, searches, alerts, and connected accounts.</div>
                                </div>
                            </button>
                            <div className="profile-section-head">
                                <h3>Session</h3>
                            </div>
                            <button
                                type="button"
                                className="profile-settings-row"
                                onClick={() => {
                                    logout();
                                    navigate('/', { replace: true });
                                }}
                            >
                                <div className="profile-settings-copy">
                                    <div className="profile-settings-title" style={{ color: '#f87171' }}>Sign out</div>
                                    <div className="profile-settings-hint">Also available in the header menu</div>
                                </div>
                            </button>
                            {dangerError ? <p className="profile-error">{dangerError}</p> : null}
                        </div>
                    )}

                    {sheet === 'identity' ? (
                        <IdentityEditor
                            name={displayName}
                            avatarUrl={profile?.avatarUrl || null}
                            onCancel={() => setSheet(null)}
                            onSave={handleSaveIdentity}
                        />
                    ) : null}
                    {sheet === 'clear' || sheet === 'delete' ? (
                        <div className="profile-sheet" role="dialog">
                            <h3>{sheet === 'clear' ? 'Clear search history?' : 'Delete your account?'}</h3>
                            <p className="profile-hint">
                                {sheet === 'clear'
                                    ? 'Taste chips reset. Subscriptions and connections stay.'
                                    : 'This deletes your LiveNow account and personal data. It cannot be undone. If delete is unavailable, email livenow.enterprise@gmail.com.'}
                            </p>
                            <div className="profile-sheet-actions">
                                <button type="button" className="profile-btn" onClick={() => setSheet(null)} disabled={dangerBusy}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="profile-btn profile-btn--danger"
                                    onClick={sheet === 'clear' ? handleClearHistory : handleDeleteData}
                                    disabled={dangerBusy}
                                >
                                    {dangerBusy ? 'Working...' : sheet === 'clear' ? 'Clear history' : 'Delete account'}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <button
                className="results-back-fab"
                onClick={() => navigate('/')}
                aria-label="Back to home"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <LegalFooter />
        </div>
    );
};

export default Profile;
